import React, { useState, useEffect } from 'react';
import { Button } from './ui/Button';
import {
  Key,
  Plus,
  Copy,
  Trash2,
  Eye,
  EyeOff,
  Loader2,
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../store/authStore';
import { format } from 'date-fns';
import { useTranslation } from '../lib/i18n';

interface APIKey {
  id: string;
  key: string;
  name: string | null;
  is_active: boolean;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
}

export const APIKeyManagement = () => {
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [showNewKeyDialog, setShowNewKeyDialog] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (user) {
      loadAPIKeys();
    }
  }, [user]);

  const loadAPIKeys = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('api_keys')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setApiKeys((data || []).filter(key => key !== null));
    } catch (error: any) {
      console.error('Error loading API keys:', error);
      setMessage({
        text: t('apiKeys.failedLoad', { error: error.message || t('apiKeys.unknownError') }),
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const generateSecureKey = (): string => {
    const prefix = 'dhk';
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const length = 48;
    let result = '';
    const randomValues = new Uint8Array(length);
    crypto.getRandomValues(randomValues);

    for (let i = 0; i < length; i++) {
      result += chars[randomValues[i] % chars.length];
    }

    return `${prefix}_${result}`;
  };

  const handleCreateAPIKey = async () => {
    if (!user?.id) return;

    if (!newKeyName.trim()) {
      setMessage({
        text: t('apiKeys.nameRequired'),
        type: 'error'
      });
      return;
    }

    try {
      setCreating(true);
      setMessage(null);

      const newKey = generateSecureKey();

      const { data, error } = await supabase
        .from('api_keys')
        .insert([
          {
            key: newKey,
            user_id: user.id,
            name: newKeyName.trim(),
            is_active: true
          }
        ])
        .select()
        .single();

      if (error) throw error;

      setNewlyCreatedKey(newKey);
      setApiKeys([data, ...apiKeys]);
      setNewKeyName('');
      setMessage({
        text: t('apiKeys.created'),
        type: 'success'
      });

      setTimeout(() => setMessage(null), 5000);
    } catch (error: any) {
      console.error('Error creating API key:', error);
      setMessage({
        text: t('apiKeys.failedCreate', { error: error.message || t('apiKeys.unknownError') }),
        type: 'error'
      });
    } finally {
      setCreating(false);
    }
  };

  const handleRevokeAPIKey = async (keyId: string) => {
    if (!confirm(t('apiKeys.confirmRevoke'))) {
      return;
    }

    try {
      setMessage(null);

      const { error } = await supabase
        .from('api_keys')
        .update({ is_active: false })
        .eq('id', keyId)
        .eq('user_id', user?.id);

      if (error) throw error;

      setApiKeys(apiKeys.map(key =>
        key.id === keyId ? { ...key, is_active: false } : key
      ));

      setMessage({
        text: t('apiKeys.revoked'),
        type: 'success'
      });

      setTimeout(() => setMessage(null), 5000);
    } catch (error: any) {
      console.error('Error revoking API key:', error);
      setMessage({
        text: t('apiKeys.failedRevoke', { error: error.message || t('apiKeys.unknownError') }),
        type: 'error'
      });
    }
  };

  const handleDeleteAPIKey = async (keyId: string) => {
    if (!confirm(t('apiKeys.confirmDelete'))) {
      return;
    }

    try {
      setMessage(null);

      const { error } = await supabase
        .from('api_keys')
        .delete()
        .eq('id', keyId)
        .eq('user_id', user?.id);

      if (error) throw error;

      setApiKeys(apiKeys.filter(key => key.id !== keyId));

      setMessage({
        text: t('apiKeys.deleted'),
        type: 'success'
      });

      setTimeout(() => setMessage(null), 5000);
    } catch (error: any) {
      console.error('Error deleting API key:', error);
      setMessage({
        text: t('apiKeys.failedDelete', { error: error.message || t('apiKeys.unknownError') }),
        type: 'error'
      });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setMessage({
      text: t('apiKeys.copied'),
      type: 'success'
    });
    setTimeout(() => setMessage(null), 3000);
  };

  const toggleKeyVisibility = (keyId: string) => {
    setVisibleKeys(prev => {
      const newSet = new Set(prev);
      if (newSet.has(keyId)) {
        newSet.delete(keyId);
      } else {
        newSet.add(keyId);
      }
      return newSet;
    });
  };

  const maskKey = (key: string): string => {
    if (key.length <= 12) return key;
    return `${key.substring(0, 8)}${'*'.repeat(key.length - 12)}${key.substring(key.length - 4)}`;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center space-x-4 mb-6">
        <div className="bg-blue-100 dark:bg-blue-900 p-3 rounded-full">
          <Key className="w-6 h-6 text-blue-500 dark:text-blue-300" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-semibold">{t('apiKeys.title')}</h2>
          <p className="text-gray-500 dark:text-gray-400">
            {t('apiKeys.desc')}
          </p>
        </div>
      </div>

      {message && (
        <div className={`flex items-center gap-2 p-4 rounded-lg mb-4 ${
          message.type === 'success'
            ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300'
            : 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
          )}
          {message.text}
        </div>
      )}

      {newlyCreatedKey && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-yellow-900 dark:text-yellow-100 mb-2">
                {t('apiKeys.saveKeyTitle')}
              </h3>
              <p className="text-sm text-yellow-800 dark:text-yellow-200 mb-3">
                {t('apiKeys.saveKeyDesc')}
              </p>
              <div className="flex items-center gap-2 bg-white dark:bg-gray-800 p-3 rounded border border-yellow-300 dark:border-yellow-600">
                <code className="flex-1 text-sm font-mono break-all">
                  {newlyCreatedKey}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyToClipboard(newlyCreatedKey)}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setNewlyCreatedKey(null)}
                className="mt-3"
              >
                {t('apiKeys.savedKey')}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4 mb-6">
        {!showNewKeyDialog ? (
          <Button onClick={() => setShowNewKeyDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {t('apiKeys.generateNew')}
          </Button>
        ) : (
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                {t('apiKeys.keyName')}
              </label>
              <input
                type="text"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder={t('apiKeys.keyNamePlaceholder')}
                className="w-full p-2 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-800"
                disabled={creating}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('apiKeys.keyNameHint')}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleCreateAPIKey}
                disabled={creating || !newKeyName.trim()}
              >
                {creating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('apiKeys.creating')}
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    {t('apiKeys.createKey')}
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowNewKeyDialog(false);
                  setNewKeyName('');
                }}
                disabled={creating}
              >
                {t('apiKeys.cancel')}
              </Button>
            </div>
          </div>
        )}
      </div>

      {apiKeys.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <Key className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">
            {t('apiKeys.none')}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <h3 className="font-semibold text-sm text-gray-700 dark:text-gray-300">
            {t('apiKeys.yourKeys')}
          </h3>
          {apiKeys.filter(key => key && key.id).map((apiKey) => (
            <div
              key={apiKey.id}
              className={`bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border ${
                apiKey.is_active
                  ? 'border-gray-200 dark:border-gray-700'
                  : 'border-red-200 dark:border-red-900 opacity-60'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                      {apiKey.name || t('apiKeys.unnamedKey')}
                    </h4>
                    {!apiKey.is_active && (
                      <span className="text-xs px-2 py-1 bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300 rounded">
                        {t('apiKeys.revokedLabel')}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <code className="text-sm font-mono text-gray-600 dark:text-gray-400 break-all">
                      {visibleKeys.has(apiKey.id) ? apiKey.key : maskKey(apiKey.key)}
                    </code>
                    <button
                      onClick={() => toggleKeyVisibility(apiKey.id)}
                      className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      {visibleKeys.has(apiKey.id) ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => copyToClipboard(apiKey.key)}
                      className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {t('apiKeys.createdLabel', { date: format(new Date(apiKey.created_at), 'MMM d, yyyy') })}
                    </span>
                    {apiKey.last_used_at && (
                      <span>
                        {t('apiKeys.lastUsed', { date: format(new Date(apiKey.last_used_at), 'MMM d, yyyy') })}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  {apiKey.is_active && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRevokeAPIKey(apiKey.id)}
                      title={t('apiKeys.revokeTitle')}
                    >
                      {t('apiKeys.revoke')}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDeleteAPIKey(apiKey.id)}
                    title={t('apiKeys.deleteTitle')}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
        <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
          {t('apiKeys.usingKeyTitle')}
        </h4>
        <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
          {t('apiKeys.usingKeyDesc')}
        </p>
        <code className="block text-xs bg-white dark:bg-gray-800 p-3 rounded border border-blue-200 dark:border-blue-700 font-mono">
          Authorization: Bearer your_api_key_here
        </code>
        <p className="text-sm text-blue-800 dark:text-blue-200 mt-3">
          <a href="/api-docs" className="underline font-semibold">{t('apiKeys.apiDocsLink')}</a>
        </p>
      </div>
    </div>
  );
};

export default APIKeyManagement;
